import { useEffect, useState, useCallback } from "react";
import AppShell from "@/components/AppShell";
import { api } from "@/lib/api";
import { Plus, ChevronDown, Repeat, CalendarDays, Sparkles, Archive } from "lucide-react";
import SubscriptionTab from "@/components/mealplans/SubscriptionTab";
import PreorderTab from "@/components/mealplans/PreorderTab";
import SpecialsTab from "@/components/mealplans/SpecialsTab";
import CreatePlanModal from "@/components/mealplans/CreatePlanModal";

const TABS = [
  { key: "subscription", label: "Subscription Plans", icon: Repeat, accent: "slate", desc: "Locked-in term-long meal subscriptions" },
  { key: "preorder", label: "Pre-order Plans", icon: CalendarDays, accent: "amber", desc: "Daily booking, 4 PM previous-day cutoff" },
  { key: "special", label: "Special Events", icon: Sparkles, accent: "rose", desc: "One-shot festivals & CSR meals" },
];

const ACCENT_TAB = {
  slate: "bg-slate-900 text-white",
  amber: "bg-amber-700 text-white",
  rose: "bg-rose-700 text-white",
};

export default function AdminMealPlans() {
  const [tab, setTab] = useState("subscription");
  const [byFamily, setByFamily] = useState({ subscription: [], preorder: [] });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(null); // family key or null
  const [editing, setEditing] = useState(null); // { family, plan } or null
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qp = showArchived ? "?includeArchived=true" : "";
      const [bf, tpls] = await Promise.all([
        api.get(`/meal-plans/by-family${qp}`),
        api.get("/weekly-menu/templates"),
      ]);
      setByFamily(bf.data || { subscription: [], preorder: [] });
      setTemplates(Array.isArray(tpls.data) ? tpls.data : []);
    } finally {
      setLoading(false);
    }
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const activeTab = TABS.find(t => t.key === tab);

  return (
    <AppShell variant="admin">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Meal Plans</div>
            <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-ink mt-2">Three plan families, one hub</h1>
            <p className="text-sm text-ink-500 mt-1">{activeTab?.desc}</p>
          </div>
          <div className="relative flex items-center gap-2">
            <button data-testid="toggle-archived"
              onClick={() => setShowArchived(s => !s)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold uppercase tracking-wider ${showArchived ? "bg-amber-100 text-amber-800" : "bg-cream-200 text-ink-700 hover:bg-cream-300"}`}>
              <Archive size={12} /> {showArchived ? "Hiding active only" : "Show archived"}
            </button>
            <button
              data-testid="create-plan-dropdown"
              onClick={() => setCreateMenuOpen(o => !o)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-ink text-white font-semibold hover:bg-ink-700"
            >
              <Plus size={14} /> Create Plan <ChevronDown size={14} />
            </button>
            {createMenuOpen && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setCreateMenuOpen(false)} />
                <div className="absolute right-0 mt-1 z-20 bg-white border border-border rounded-2xl shadow-floating min-w-[240px] py-2">
                  {TABS.map(t => {
                    const Icon = t.icon;
                    return (
                      <button key={t.key} onClick={() => { setShowCreate(t.key); setCreateMenuOpen(false); }}
                        data-testid={`create-option-${t.key}`}
                        className="w-full text-left px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-cream-100 flex items-center gap-2">
                        <Icon size={14} /> {t.label}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Segmented control */}
        <div className="inline-flex bg-cream-200 rounded-full p-1 mb-7">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`mealplans-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-5 py-2 rounded-full text-sm font-semibold flex items-center gap-2 transition-colors ${tab === t.key ? ACCENT_TAB[t.accent] : "text-ink-700 hover:bg-cream-300"}`}
              >
                <Icon size={14} /> {t.label}
                <span className={`text-[10px] font-bold px-1.5 rounded-full ${tab === t.key ? "bg-white/20" : "bg-ink/10 text-ink-500"}`}>
                  {t.key === "subscription" ? byFamily.subscription.length :
                   t.key === "preorder" ? byFamily.preorder.length : "•"}
                </span>
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="text-sm text-ink-500">Loading…</div>
        ) : (
          <>
            {tab === "subscription" && (
              <SubscriptionTab plans={byFamily.subscription} templates={templates}
                showArchived={showArchived}
                onCreate={() => setShowCreate("subscription")}
                onEdit={(plan) => setEditing({ family: "subscription", plan })}
                onPlanUpdated={load} />
            )}
            {tab === "preorder" && (
              <PreorderTab plans={byFamily.preorder}
                showArchived={showArchived}
                onCreate={() => setShowCreate("preorder")}
                onEdit={(plan) => setEditing({ family: "preorder", plan })}
                onPlanUpdated={load} />
            )}
            {tab === "special" && (
              <SpecialsTab
                showArchived={showArchived}
                onCreate={() => setShowCreate("special")}
                onEdit={(plan) => setEditing({ family: "special", plan })}
                onPlanUpdated={load} />
            )}
          </>
        )}
      </div>

      {showCreate && (
        <CreatePlanModal
          family={showCreate}
          onClose={() => setShowCreate(null)}
          onCreated={load}
        />
      )}
      {editing && (
        <CreatePlanModal
          key={editing.plan.id}
          family={editing.family}
          existing={editing.plan}
          onClose={() => setEditing(null)}
          onCreated={load}
        />
      )}
    </AppShell>
  );
}
